import { createBrowserRouter, redirect, defer } from "react-router-dom";
import BuildMyKubota from '../container/BuildMyKubota';
import ProductSelector from '../selector/ProductSelector';
import ProductDetails from '../configurator/ProductDetails';
import bmkservice from '../service/bmk-service';
import { graphqlRequest } from '../service/graphql-client';
import { GQ_GET_BUILD } from '../service/graphql-mutations';

// Lazy-load lodash + kubota-configuration-engine the first time the productDetails
// route loader needs window.ProductConfiguration. Cached after first call so subsequent
// configurator navigations don't refetch.
let configEngineLoadPromise = null;
function ensureConfigEngineLoaded() {
    if (configEngineLoadPromise) return configEngineLoadPromise;

    const lodashUrl = process.env.REACT_APP_LODASH_URL;
    const engineUrl = process.env.REACT_APP_CONFIG_ENGINE_URL;

    function loadScript(src) {
        return new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = src;
            s.async = false; // preserve insertion order so lodash executes before config-engine
            s.onload = () => resolve();
            s.onerror = () => reject(new Error(`Failed to load ${src}`));
            document.head.appendChild(s);
        });
    }

    configEngineLoadPromise = Promise.all([loadScript(lodashUrl), loadScript(engineUrl)]);
    return configEngineLoadPromise;
}

async function sendError(message, returnToHome = false) {
    fetch(bmkservice.errorLog, {
        method: 'post',
        body: message
    });
    if (returnToHome) {
        return redirect("/");
    }
    return null;
}

function parseIdFromSlug(slug) {
    const dashIndex = slug.indexOf('-');
    if (dashIndex <= 0) return "";
    return slug.substring(0, dashIndex);
}

async function loadProducts(id) {
    if (id === "") {
        sendError('Empty product ID parsed from URL slug — possible missing or malformed product data');
        return { ProductGroups: [] };
    }
    const response = await fetch(bmkservice.getProducts + id);
    try {
        const json = await response.json();
        if (!json.ProductGroups) {
            return sendError('Invalid data from ' + bmkservice.getProducts + id, true);
        }
        return json;
    }
    catch (e) {
        return sendError('Unable to parse reponse from ' + bmkservice.getProducts + id, id !== 0);
    }
};

async function loadConfig() {
    const [configResponse, bannerResponse] = await Promise.all([
        fetch(bmkservice.config),
        fetch(bmkservice.banners)
    ]);

    const configJson = await configResponse.json();
    configJson.banners = await bannerResponse.json();
    return configJson;
};

async function loadProductDetails(id, request) {
    // Kick off the configuration-engine load alongside the API fetches so its download
    // overlaps the request latency instead of blocking after the responses arrive.
    const enginePromise = ensureConfigEngineLoaded();

    // Phase 1: Product details + equipment in parallel
    const [productResponse, equipResponse] = await Promise.all([
        fetch(bmkservice.getProductDetails + id),
        fetch(bmkservice.getProductEquipment + id)
    ]);

    const productJson = await productResponse.json();

    if (!productJson.Tractor) {
        return sendError('Invalid data from ' + bmkservice.getProductDetails + id, true);
    }

    if (!productJson.Tractor.MSRPWithoutTires) {
        sendError('No pricing from ' + bmkservice.getProductDetails + id);
    }

    if (!productJson.Tractor.ImagePath) {
        sendError('No images from ' + bmkservice.getProductDetails + id);
    }

    productJson.studios = {};
    productJson.imageMappingData = [];

    const equipment = await equipResponse.json();
    productJson.equipmentHtml = equipment.replaceAll('/images/', '/assets/kubotatractor/');

    // Phase 2: Both Sirv calls in parallel (depend on ConfigSirvLink from phase 1)
    if (productJson.Tractor.ConfigSirvLink) {
        const configSirvPath = productJson.Tractor.ConfigSirvLink.replace('keyshot-configurator.html', '');

        const [configResponse, imgResponse] = await Promise.all([
            fetch(configSirvPath + 'keyshot-configurator-modeldata.js'),
            fetch(configSirvPath + 'keyshot-configurator-imagemapping.js')
        ]);

        const configJs = await configResponse.text();
        if (configJs.indexOf('model') === 0) {
            const model = new Function(configJs + '; return model;')();
            model.studios.forEach((studio) => {
                productJson.studios[studio.name] = { icon: configSirvPath + 'icons/' + encodeURIComponent(studio.icon), id: studio.id };
            });
        }

        const imgJs = await imgResponse.text();
        if (imgJs.indexOf('imagemappingdata') === 0) {
            const imagemappingdata = new Function(imgJs + '; return imagemappingdata;')();
            productJson.imageMappingData = imagemappingdata;
        }
    }

    await enginePromise; // window.ProductConfiguration is defined by kubota-configuration-engine.js
    productJson.configuration = new window.ProductConfiguration(productJson.Tractor, productJson.RuleList);
    if (productJson.Tractor.DefaultTires) {
        productJson.configuration.addItem(productJson.Tractor.DefaultTires);
    }

    const buildId = new URL(request.url).searchParams.get('build-id');
    if (buildId) {
        // Get access token from localStorage or Redux store
        const accessToken = localStorage.getItem('access_token') || 
                           (window.__REDUX_STORE__ && window.__REDUX_STORE__.getState().auth.accessToken);
        
        try {
            // Use GraphQL query to get single build
            const data = await graphqlRequest(GQ_GET_BUILD, { id: buildId }, accessToken);

            if (data.build && data.build.configuredItems) {
                // configuration.configuredItems (see saveBuild in ModalProvider.js) is a
                // plain dictionary of raw add-on objects keyed by AttachId — not the engine's
                // internal ConfiguredItem.ChildItems tree. Restore each item through the same
                // addItem()/addPackage() API every other add-on flow uses (ProductDetails.js),
                // rather than assigning the saved dictionary directly onto the internal tree,
                // which left the engine's package/pricing/rule state uninitialized and broke
                // print and quote submission for loaded builds.
                const savedItems = JSON.parse(data.build.configuredItems);
                Object.values(savedItems).forEach((item) => {
                    if (item.package) {
                        productJson.configuration.addPackage(item.package);
                    } else {
                        productJson.configuration.addItem(item, item.Quantity || 1);
                    }
                });
            }
        } catch (error) {
            console.error('Error loading build:', error);
            // Continue without loading build configuration if there's an error
        }
    }
    const configSirvLink = productJson.Tractor.ConfigSirvLink || '';
    productJson.baseUrl = configSirvLink.replace('keyshot-configurator.html', '');
    // Validate XRSirvLink serves an HTML viewer, not a raw .spin binary (triggers download)
    if (productJson.Tractor.XRSirvLink) {
        try {
            const headRes = await fetch(productJson.Tractor.XRSirvLink, { method: 'HEAD' });
            const contentType = headRes.headers.get('Content-Type') || '';
            if (!contentType.includes('text/html')) {
                productJson.Tractor.XRSirvLink = '';
            }
        } catch {
            productJson.Tractor.XRSirvLink = '';
        }
    }

    //productJson.AttachHierarchy.LandPrideGroup = null; // temporarily disable landpride addons

    return productJson;
};

const Router = createBrowserRouter([{
    path: "/",
    element: <BuildMyKubota />,
    id: "root",
    loader: loadConfig,
    children: [
        {
            path: "",
            element: <ProductSelector />,
            loader: () => {
                return defer({ products: loadProducts(0) });
            }
        },
        {
            path: ":productSlug",
            element: <ProductSelector />,
            loader: ({ params }) => {
                return defer({ products: loadProducts(parseIdFromSlug(params.productSlug)) });
            }
        },
        {
            path: ":productSlug/:seriesSlug",
            element: <ProductSelector />,
            loader: ({ params }) => {
                return defer({ products: loadProducts(parseIdFromSlug(params.seriesSlug)) });
            }
        },
        {
            path: ":productSlug/:seriesSlug/:modelName",
            element: <ProductDetails />,
            id: "productDetails",
            loader: ({ params, request }) => {
                return loadProductDetails(params.modelName, request);
            },
            children: [
                { path: ":pageName" }
            ]
        }
    ]
}], {basename: "/"});

export default Router;